import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Modal, TextField } from '../../components/shared';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import {
  allServicesAPI,
  creatNewServiceAPI,
  updateServiceAPI,
} from '../../redux/features/services/servicesSlice';

//Local Imports
type AddNewServiceProps = {
  isOpen: boolean | undefined;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  from: 'add' | 'edit'; // enum ['profile', 'service'];
  restProps?: { [key: string]: any };
};

type ServiceData = {
  serviceName: string;
  description: string;
  status: boolean;
  duration: string; // in minutes
  price: string; // currency value
};

const initialState = {
  serviceName: '',
  description: '',
  status: true,
  duration: '',
  price: '',
};

const AddNewServiceModal: FC<AddNewServiceProps> = ({
  isOpen,
  setIsOpen,
  from,
  restProps,
}) => {
  // REDUX
  const { addServiceLoading } = useAppSelector((state) => state.allServices);
  const dispatch = useAppDispatch();

  //STATES
  const [serviceData, setServiceData] = useState<ServiceData>(initialState);
  const [isEmptyFields, setIsEmptyFields] = useState<any>({});

  // HANDLE ON CHANGE
  const handleOnChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    e.preventDefault();
    const { name, value } = e.target;

    setServiceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Empty Check Fn
  const emptyCheck = useCallback(() => {
    setIsEmptyFields({});
    const fields = { ...serviceData };
    let isEmpty: any = {};
    Object.entries(fields).forEach(([key, value]) => {
      if (!value || value === '') isEmpty[key] = true;
    });
    setIsEmptyFields(isEmpty);
    return isEmpty ? (Object.keys(isEmpty).length > 0 ? true : false) : false;
  }, [serviceData]);

  // HANDLE SAVE SERVICE
  const handleSaveService = useCallback(async () => {
    if (emptyCheck()) return;
    const payload = {
      name: serviceData.serviceName,
      description: serviceData.description,
      status: serviceData.status,
      duration: serviceData.duration ? Number(serviceData.duration) : undefined,
      price: serviceData.price ? Number(serviceData.price) : undefined,
    };

    const result = await dispatch(creatNewServiceAPI(payload));
    if (result.meta.requestStatus === 'fulfilled') {
      setIsOpen && setIsOpen(false);
      setServiceData(initialState);
      dispatch(allServicesAPI());
    }
  }, [serviceData, dispatch, setIsOpen, emptyCheck]);

  // HANDLE SAVE SERVICE
  const handleUpdateService = useCallback(async () => {
    if (emptyCheck()) return;
    const payload = {
      name: serviceData.serviceName,
      description: serviceData.description,
      status: serviceData.status,
      duration: serviceData.duration ? Number(serviceData.duration) : undefined,
      price: serviceData.price ? Number(serviceData.price) : undefined,
      id: restProps?.row?.id,
    };
    const data = await dispatch(updateServiceAPI(payload));
    if (data.meta.requestStatus === 'fulfilled') {
      setIsOpen && setIsOpen(false);
      setServiceData(initialState);
      dispatch(allServicesAPI());
    }
  }, [serviceData, dispatch, setIsOpen, emptyCheck, restProps?.row?.id]);

  // HANDLE  SUBMIT
  const handleSubmit = useCallback(() => {
    if (from === 'add') handleSaveService();
    if (from === 'edit') handleUpdateService();
  }, [from, handleSaveService, handleUpdateService]);

  //If from is edit
  useEffect(() => {
    if (from === 'edit' && restProps?.row) {
      setServiceData({
        description: restProps?.row?.description,
        serviceName: restProps?.row?.serviceName,
        status: restProps?.row?.status,
        duration: String(restProps?.row?.rawDuration ?? ''),
        price: String(restProps?.row?.rawPrice ?? ''),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, restProps?.row]);

  if (!isOpen) {
    return null;
  }
  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen && setIsOpen(!isOpen)}
      title={from === 'add' ? 'Add New Service' : 'Edit Service'}
    >
      <div className='flex flex-col gap-4  items-start mt-2'>
        <TextField
          placeholder='Service Name'
          name='serviceName'
          value={serviceData.serviceName}
          onChange={handleOnChange}
          error={isEmptyFields?.serviceName}
          errorMsg={isEmptyFields?.serviceName && 'Service Name is required'}
        />
        <label className='w-full '>
          <textarea
            placeholder='Description'
            name='description'
            value={serviceData.description}
            className={`text-gray-700 bg-white border rounded-lg w-full p-3 focus:border-primary/40  focus:ring-primary/30 focus:outline-none focus:ring-1 focus:ring-opacity-60 
            ${
              isEmptyFields?.description &&
              'border-red-400 placeholder:text-red-400 focus:border-red-400  focus:ring-red-400 focus:outline-none focus:ring-0 focus:ring-opacity-60'
            }
            `}
            rows={5}
            onChange={handleOnChange}
          />
          {
            // !focus &&
            isEmptyFields?.description && (
              <p className='text-red-400 text-xs ml-1 '>
                Service Description is required
              </p>
            )
          }
        </label>

        <div className='w-full grid grid-cols-1 md:grid-cols-2 gap-4'>
          <TextField
            placeholder='Duration (minutes)'
            name='duration'
            type='number'
            value={serviceData.duration}
            onChange={handleOnChange}
            error={isEmptyFields?.duration}
            errorMsg={isEmptyFields?.duration && 'Duration is required'}
          />
          <TextField
            placeholder='Price'
            name='price'
            type='number'
            value={serviceData.price}
            onChange={handleOnChange}
            error={isEmptyFields?.price}
            errorMsg={isEmptyFields?.price && 'Price is required'}
          />
        </div>

        <label className='flex items-center space-x-3 ml-2'>
          <input
            checked={serviceData.status}
            type='checkbox'
            onChange={(event) =>
              setServiceData((prev) => ({
                ...prev,
                status: event.target.checked,
              }))
            }
            className='h-5 w-5 text-primary border-gray-300 rounded focus:ring-primary/30 focus:ring-opacity-60'
          />
          <span className='text-gray-700'>
            {serviceData.status ? 'Active' : 'In-active'}
          </span>
        </label>
        <div className='w-full flex justify-end'>
          <Button
            variant='primary'
            onClick={handleSubmit}
            loading={addServiceLoading}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddNewServiceModal;
